// Purchase orders (M6): by status, with the supplier, when they are expected (and whether
// they are late, or must go out today to arrive in time), how far they have arrived and their
// totals (ex VAT and with VAT, from the VAT engine). An order is made when a manager approves
// a purchase request in the approvals inbox (D-060); it is sent by a person (printed, saved
// as PDF or downloaded): nothing here sends anything to a supplier. A cancelled order's total
// is struck through: nothing is owed (UI6A-5). An order with a delivery held for a manager
// (D-066) says so instead of "late" (UI6A-102). Below 1360 px, Arrived folds under the status
// (the production tables' priority columns, UI6A-104).

import type { PoSummary } from '@enp/domain/api';
import { localDate } from '@enp/domain/time';
import {
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  Money,
  PageHeader,
  type ColumnDef,
} from '@enp/ui';
import { useInfiniteQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ClipboardList, Lightbulb, Plus, SearchX, X } from 'lucide-react';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { NARROW_ONLY, WIDE_COLUMN } from '../../../production/parts.js';
import { arrivedText, isOwed, orderTiming } from '../../../purchasing/figures.js';
import {
  KeyHints,
  PoStatusBadge,
  PurchasingViews,
  TableSkeleton,
  useCan,
  usePageKeys,
} from '../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  heldDeliveriesQuery,
  ORDER_VIEWS,
  type OrdersSearch,
  ordersQuery,
  ordersSearchSchema,
} from '../../../purchasing/queries.js';
import { ORDER_VIEW_LABEL, PO_STATUS } from '../../../purchasing/words.js';
import { useAccountTimeZone } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { DayRange, FilterSelect, LoadMoreFooter } from '../../../stock/components.js';
import { plainDateLabel, accountToday } from '../../../stock/labels.js';
import { clean } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/purchasing/')({
  validateSearch: ordersSearchSchema,
  component: OrdersPage,
});

const TABLE_LABEL = 'Purchase orders';
const NBSP = String.fromCharCode(0xa0);
const col = createColumnHelper<PoSummary>();

const TIMING_TONE = {
  danger: 'font-medium text-state-danger',
  warning: 'font-medium text-state-warning',
  neutral: 'text-muted',
} as const;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Column = ColumnDef<PoSummary, any>;

const columns = (
  today: string,
  leadOf: (supplierId: string) => number | null,
  dayOf: (instant: string) => string,
  heldOf: (orderId: string) => number,
): Column[] => [
  col.display({
    id: 'number',
    header: 'Order',
    meta: { width: 'min-w-40' },
    cell: ({ row }) => (
      <span className="flex flex-col leading-tight">
        <Link
          to="/purchasing/orders/$orderId"
          params={{ orderId: row.original.id }}
          tabIndex={-1}
          className={cn(
            'whitespace-nowrap font-mono text-sm font-medium hover:underline',
            isOwed(row.original.status) ? 'text-fg' : 'text-muted',
          )}
        >
          {row.original.number}
        </Link>
        <span className="text-xs text-muted">
          {row.original.purchase_request ? (
            <>
              From request{' '}
              <span className="whitespace-nowrap">{row.original.purchase_request.number}</span>
            </>
          ) : (
            'Made without a request'
          )}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'supplier',
    header: 'Supplier',
    cell: ({ row }) => (
      <span
        className={cn(
          'whitespace-nowrap font-medium',
          isOwed(row.original.status) ? 'text-fg' : 'text-muted',
        )}
      >
        {row.original.supplier.name}
      </span>
    ),
  }),
  col.display({
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span className="flex flex-col items-start gap-1 leading-tight">
        <PoStatusBadge status={row.original.status} />
        {/* Arrived, folded under the status below 1360 px (UI6A-104). */}
        <span
          className={cn('max-w-56 text-xs text-muted', NARROW_ONLY)}
          data-testid="arrived-folded"
        >
          {arrivedText(row.original, dayOf, heldOf(row.original.id))}
        </span>
      </span>
    ),
  }),
  col.display({
    id: 'expected',
    header: 'Expected',
    cell: ({ row }) => {
      const po = row.original;
      const t = orderTiming(
        po,
        today,
        leadOf(po.supplier.id),
        (d) => plainDateLabel(d, today),
        heldOf(po.id),
      );
      return (
        <span className="flex flex-col leading-tight">
          {po.expected_date ? (
            <time dateTime={po.expected_date} className="whitespace-nowrap tabular-nums">
              {plainDateLabel(po.expected_date, today)}
            </time>
          ) : (
            <span className="text-sm text-muted">No date</span>
          )}
          {t && (
            <span
              className={cn('whitespace-nowrap text-xs', TIMING_TONE[t.tone])}
              data-testid="order-timing"
            >
              {t.text}
            </span>
          )}
        </span>
      );
    },
  }),
  col.display({
    id: 'received',
    header: 'Arrived',
    meta: { className: WIDE_COLUMN },
    cell: ({ row }) => (
      // Wraps within its width: a held delivery's note never pushes Total out (UI6A-104).
      <span className="block max-w-52 text-sm text-fg" data-testid="arrived">
        {arrivedText(row.original, dayOf, heldOf(row.original.id))}
      </span>
    ),
  }),
  col.display({
    id: 'total',
    header: 'Total',
    meta: { align: 'right' },
    cell: ({ row }) => {
      const po = row.original;
      const owed = isOwed(po.status);
      return (
        <span className="flex flex-col items-end leading-tight" data-testid="order-total">
          <Money
            cents={po.total_cents}
            className={cn('font-semibold', owed ? 'text-fg' : 'text-muted line-through')}
          />
          {owed ? (
            <span className="whitespace-nowrap text-xs text-muted">
              <Money cents={po.subtotal_ex_vat_cents} /> ex VAT
            </span>
          ) : (
            <span className="whitespace-nowrap text-xs font-medium text-fg">Not owed</span>
          )}
          {owed && po.unpriced_lines > 0 && (
            <span className="whitespace-nowrap text-xs font-medium text-state-warning">
              {po.unpriced_lines} of {po.line_count} {po.line_count === 1 ? 'line' : 'lines'}{' '}
              unpriced, not in total
            </span>
          )}
        </span>
      );
    },
  }),
  col.display({
    id: 'open',
    header: () => <span className="sr-only">Open</span>,
    meta: { width: 'w-8' },
    cell: () => <ChevronRight aria-hidden className="size-4 text-muted" />,
  }),
];

const SKELETON = [
  { header: 'Order', className: 'min-w-40', twoLines: true },
  { header: 'Supplier' },
  { header: 'Status', twoLines: true },
  { header: 'Expected', twoLines: true },
  { header: 'Arrived', className: WIDE_COLUMN },
  { header: 'Total', align: 'right', twoLines: true },
  { header: '', className: 'w-8', empty: true },
] as const;

function OrdersPage() {
  useDocumentTitle('Purchase orders');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const timeZone = useAccountTimeZone() ?? me.account.timezone;
  const canRequest = useCan('purchasing.request');
  const suppliers = useQuery(allSuppliersQuery());
  const list = useInfiniteQuery(ordersQuery(search));
  const held = useQuery(heldDeliveriesQuery());
  const heldByOrder = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of held.data?.items ?? []) if (h.po) m.set(h.po.id, (m.get(h.po.id) ?? 0) + 1);
    return m;
  }, [held.data]);
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const view = search.view ?? 'expected';
  const filtered = Boolean(search.supplier || search.from || search.to);
  const leads = useMemo(
    () => new Map((suppliers.data?.items ?? []).map((s) => [s.id, s.lead_time_days])),
    [suppliers.data],
  );
  const cols = useMemo(
    () =>
      columns(
        today,
        (id) => leads.get(id) ?? null,
        // "29 Sep" never splits over two lines in the folded text.
        (instant) => plainDateLabel(localDate(instant, timeZone), today).replace(/ /g, NBSP),
        (id) => heldByOrder.get(id) ?? 0,
      ),
    [today, leads, timeZone, heldByOrder],
  );

  const newRequest = () => void navigate({ to: '/purchasing/requests/new' });
  usePageKeys({ n: canRequest ? newRequest : undefined });

  const setSearch = (patch: Partial<OrdersSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const empty =
    filtered || view !== 'expected' ? (
      <EmptyState
        compact
        icon={SearchX}
        title={
          view === 'expected'
            ? 'No expected orders match'
            : view === 'every'
              ? 'No orders match'
              : `No orders ${ORDER_VIEW_LABEL[view].toLowerCase()}`
        }
        description={
          view === 'expected'
            ? 'No order still waiting for a delivery fits these filters.'
            : view === 'every'
              ? 'No order fits these filters.'
              : `${PO_STATUS[view].means} No order fits that.`
        }
        action={
          <Button
            variant="secondary"
            icon={<X aria-hidden />}
            onClick={() => void navigate({ search: {} })}
          >
            Show expected orders
          </Button>
        }
      />
    ) : (
      <EmptyState
        icon={ClipboardList}
        title="No orders waiting for a delivery"
        description="An order is made when a manager approves a purchase request in the approvals inbox. Start from the restock suggestion, or ask for stock yourself."
        action={
          <>
            <Button asChild>
              <Link to="/purchasing/restock">
                <Lightbulb aria-hidden />
                Open the restock suggestion
              </Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to="/purchasing" search={{ view: 'every' }}>
                See every order
              </Link>
            </Button>
          </>
        }
      />
    );

  return (
    <>
      <PageHeader
        title="Purchasing"
        description="Orders to suppliers, what has arrived against them, and what to order next. Nothing here sends anything to a supplier: you send an order yourself and mark it sent."
        actions={
          canRequest ? (
            <Button asChild>
              <Link to="/purchasing/requests/new" aria-keyshortcuts="n">
                <Plus aria-hidden />
                New purchase request
              </Link>
            </Button>
          ) : undefined
        }
      />
      <PurchasingViews current="orders" />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap items-center gap-2" role="search" aria-label="Order filters">
          <FilterSelect
            label="Status"
            value={view === 'expected' ? undefined : view}
            allLabel="Expected (still to arrive)"
            options={ORDER_VIEWS.filter((v) => v !== 'expected').map((v) => ({
              value: v,
              label: ORDER_VIEW_LABEL[v],
            }))}
            onChange={(v) => setSearch({ view: v as OrdersSearch['view'] })}
            // "All statuses" widens the list: it isn't a narrowing filter, so it isn't tinted.
            className={
              view === 'every'
                ? 'data-active:border-border data-active:bg-surface data-active:text-fg'
                : undefined
            }
          />
          <FilterSelect
            label="Supplier"
            value={search.supplier}
            allLabel="All suppliers"
            options={(suppliers.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))}
            onChange={(v) => setSearch({ supplier: v })}
          />
          <DayRange
            idPrefix="orders-expected"
            from={search.from}
            to={search.to}
            today={today}
            onChange={(r) => setSearch(r)}
          />
          {filtered && (
            <Button
              variant="ghost"
              icon={<X aria-hidden />}
              onClick={() => setSearch({ supplier: undefined, from: undefined, to: undefined })}
            >
              Clear
            </Button>
          )}
        </div>
        <span className="ml-auto">
          <KeyHints keys={canRequest ? [['n', 'new request']] : []} />
        </span>
      </div>
      {(search.from || search.to) && (
        <p className="mb-3 text-sm text-muted">Dates filter on the day each order is expected.</p>
      )}
      {list.isError && !list.data ? (
        <ErrorPanel
          error={list.error}
          titleAs="h2"
          what="The orders"
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <TableSkeleton label={TABLE_LABEL} columns={SKELETON} />
      ) : (
        <DataTable
          label={TABLE_LABEL}
          columns={cols}
          data={items}
          loading={false}
          empty={empty}
          filterable={false}
          getRowId={(o) => o.id}
          onRowActivate={(o) =>
            void navigate({ to: '/purchasing/orders/$orderId', params: { orderId: o.id } })
          }
          footer={
            items.length > 0 ? (
              <LoadMoreFooter
                loaded={items.length}
                noun={['order', 'orders']}
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                updating={list.isFetching && !list.isFetchingNextPage}
                failed={list.isError && Boolean(list.data)}
                onMore={() => void list.fetchNextPage()}
              />
            ) : undefined
          }
          className="xl:max-h-[calc(100dvh-22rem)]"
        />
      )}
      <p className="mt-4 text-sm text-muted">
        Totals include VAT where the supplier charges it; the figure under each is ex VAT. “Send by”
        uses each supplier’s lead time.
      </p>
    </>
  );
}
